import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import taskManagerDictsService, {
  type TaskManagerDictItem,
} from '../services/taskManagerDicts.service';
import { createDedupedCache, type DedupedCache } from '../utils/dedupedCache';
import { useAppFeatures } from './AppFeaturesContext';

/**
 * Registry статичных task-manager словарей:
 *   - priorities (задачи: low/med/high/urgent)
 *   - dependency types (blocks/blocked_by/related_to/etc)
 *   - operation types (created/updated/status_changed/etc — для task history)
 *
 * Task statuses НЕ здесь — они per-org, живут в OrgWorkflowContext.
 * Assignment roles НЕ здесь — отдельный AssignmentRolesContext (старше).
 *
 * TTL 10 мин — словари меняются очень редко (новый priority code — редкая операция).
 */

const TTL_MS = 10 * 60_000;

interface Caches {
  priorities: DedupedCache<string, readonly TaskManagerDictItem[]>;
  dependencyTypes: DedupedCache<string, readonly TaskManagerDictItem[]>;
  operationTypes: DedupedCache<string, readonly TaskManagerDictItem[]>;
}

interface TaskManagerDictsContextValue {
  priorities: readonly TaskManagerDictItem[];
  dependencyTypes: readonly TaskManagerDictItem[];
  operationTypes: readonly TaskManagerDictItem[];
  loading: boolean;
  error: Error | null;
  reload: () => Promise<void>;
  priorityByCode: (code: string | null | undefined) => TaskManagerDictItem | undefined;
  dependencyTypeById: (id: number) => TaskManagerDictItem | undefined;
  operationTypeByCode: (code: string | null | undefined) => TaskManagerDictItem | undefined;
}

const Ctx = createContext<TaskManagerDictsContextValue | null>(null);

export function TaskManagerDictsProvider({ children }: { children: React.ReactNode }) {
  const appFeatures = useAppFeatures();
  const taskFeatureEnabled = appFeatures.task === true;
  const cachesRef = useRef<Caches | null>(null);
  if (!cachesRef.current) {
    cachesRef.current = {
      priorities: createDedupedCache({
        fetcher: () => taskManagerDictsService.listPriorities().then((r) => Object.freeze(r)),
        ttlMs: TTL_MS,
      }),
      dependencyTypes: createDedupedCache({
        fetcher: () => taskManagerDictsService.listDependencyTypes().then((r) => Object.freeze(r)),
        ttlMs: TTL_MS,
      }),
      operationTypes: createDedupedCache({
        fetcher: () => taskManagerDictsService.listOperationTypes().then((r) => Object.freeze(r)),
        ttlMs: TTL_MS,
      }),
    };
  }

  const [priorities, setPriorities] = useState<readonly TaskManagerDictItem[]>([]);
  const [dependencyTypes, setDependencyTypes] = useState<readonly TaskManagerDictItem[]>([]);
  const [operationTypes, setOperationTypes] = useState<readonly TaskManagerDictItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const load = useCallback(async (force = false) => {
    if (!cachesRef.current) return;
    if (!taskFeatureEnabled) {
      // Фича задач выключена — не дёргаем словари, пустого state достаточно.
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [p, d, o] = await Promise.all([
        force ? cachesRef.current.priorities.refetch('all') : cachesRef.current.priorities.ensureLoaded('all'),
        force ? cachesRef.current.dependencyTypes.refetch('all') : cachesRef.current.dependencyTypes.ensureLoaded('all'),
        force ? cachesRef.current.operationTypes.refetch('all') : cachesRef.current.operationTypes.ensureLoaded('all'),
      ]);
      setPriorities(p);
      setDependencyTypes(d);
      setOperationTypes(o);
    } catch (e) {
      setError(e instanceof Error ? e : new Error(String(e)));
    } finally {
      setLoading(false);
    }
  }, [taskFeatureEnabled]);

  const reload = useCallback(() => load(true), [load]);

  useEffect(() => {
    void load();
  }, [load]);

  const prioritiesByCode = useMemo(
    () => new Map(priorities.filter((p) => p.code !== null).map((p) => [p.code as string, p])),
    [priorities],
  );
  const dependencyTypesById = useMemo(
    () => new Map(dependencyTypes.map((d) => [d.id, d])),
    [dependencyTypes],
  );
  const operationTypesByCode = useMemo(
    () => new Map(operationTypes.filter((o) => o.code !== null).map((o) => [o.code as string, o])),
    [operationTypes],
  );

  const priorityByCode = useCallback(
    (code: string | null | undefined) => (code ? prioritiesByCode.get(code) : undefined),
    [prioritiesByCode],
  );
  const dependencyTypeById = useCallback(
    (id: number) => dependencyTypesById.get(id),
    [dependencyTypesById],
  );
  const operationTypeByCode = useCallback(
    (code: string | null | undefined) => (code ? operationTypesByCode.get(code) : undefined),
    [operationTypesByCode],
  );

  const value = useMemo<TaskManagerDictsContextValue>(
    () => ({
      priorities,
      dependencyTypes,
      operationTypes,
      loading,
      error,
      reload,
      priorityByCode,
      dependencyTypeById,
      operationTypeByCode,
    }),
    [priorities, dependencyTypes, operationTypes, loading, error, reload, priorityByCode, dependencyTypeById, operationTypeByCode],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useTaskManagerDicts(): TaskManagerDictsContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useTaskManagerDicts must be used inside <TaskManagerDictsProvider>');
  return v;
}
